import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import { useCallback, useState } from "react";
import { MdOutlineModeEditOutline, MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";
import { Link } from "react-router-dom";
import { ActionIcon, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import Page from '../../../layout/Page/Page';
import { menuRoutes } from "../../../router/menu";
import { InspectorCredentialsService } from "../../../services/product/inspectorCredentialsService";
import { InspectorCredentialsApiResponse } from "../../../type/entities/inspector-credentials-type";

const columnOrder = [
    'id',
    'product',
    'organisingCorporation',
    'platformCif',
    'platformBusinessName',
    'platformUrl',
    'inspectorUser',
    'inspectorPassword',
    'updatedAt',
    'createdAt'
];

const ensureExternalUrl = (url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
};

const InspectorCredentialsList = () => {
    const service = new InspectorCredentialsService();
    const { handleErrors } = useHandleErrors();

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [passwordShown, setPasswordShown] = useState<Array<string>>([]);

    const [data, loading, , refetch] = useFetch(useCallback(async () => {
        const response = await service.list(filters);
        return response.getResponseData() as InspectorCredentialsApiResponse;
    }, [filters]));

    const listResult = data?.data ?? [];
    const orderedData = () => listResult.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.delete(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Credencial eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const editBase = `${menuRoutes.products.path}${menuRoutes.products.inspectorCredentials}`;

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'inspector-credentials-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={['id']}
                    overrideColumns={[
                        {
                            key: 'product',
                            render: (row: any) => {
                                const p = row.row.original.product;
                                if (!p) return 'N/A';
                                return `${p.code ? p.code + ' - ' : ''}${p.name ?? ''}`.trim() || 'N/A';
                            },
                        },
                        {
                            key: 'organisingCorporation',
                            columnNameKey: 'Entidad organizadora',
                            render: (row: any) => row.row.original.organisingCorporation?.name ?? 'N/A',
                        },
                        {
                            key: 'platformCif',
                            columnNameKey: 'CIF plataforma',
                            render: (row: any) => row.row.original.platformCif ?? '—',
                        },
                        {
                            key: 'platformBusinessName',
                            columnNameKey: 'Razón social plataforma',
                            render: (row: any) => row.row.original.platformBusinessName ?? '—',
                        },
                        {
                            key: 'platformUrl',
                            columnNameKey: 'URL plataforma',
                            render: (row: any) => {
                                const platformUrl = row.row.original.platformUrl;
                                if (!platformUrl) return '—';
                                return (
                                    <a
                                        href={ensureExternalUrl(platformUrl)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary underline break-all"
                                    >
                                        {platformUrl}
                                    </a>
                                );
                            },
                        },
                        {
                            key: 'inspectorUser',
                            columnNameKey: 'Usuario inspector',
                            render: (row: any) => row.row.original.inspectorUser ?? '—',
                        },
                        {
                            key: 'inspectorPassword',
                            columnNameKey: 'Contraseña inspector',
                            // Render like a password with a eye icon to show the password
                            render: (row: any) => {
                                let password = row.row.original.inspectorPassword;
                                let showPassword = passwordShown.includes(row.row.original.id);
                                return (
                                    <div className="flex items-center gap-2">
                                        <ActionIcon as="span" size="sm" variant="outline" className="hover:!border-gray-900 hover:text-gray-700" onClick={() => {   
                                            if (showPassword) {
                                                setPasswordShown(passwordShown.filter((id) => id !== row.row.original.id));
                                            } else {
                                                setPasswordShown([...passwordShown, row.row.original.id]);
                                            }
                                        }}>
                                            {showPassword ? <MdOutlineVisibilityOff className="h-4 w-4" size={20} /> : <MdOutlineVisibility className="h-4 w-4" size={20} />}
                                        </ActionIcon>
                                        <span className="text-gray-500">{showPassword ? password : '*************'}</span>
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const dateVal = row.row.original?.updatedAt;
                                const dateStr = typeof dateVal === 'object' && dateVal?.date ? dateVal.date : dateVal;
                                if (!dateStr) return 'N/A';
                                const parsed = new Date(dateStr);
                                return !isNaN(parsed.getTime()) ? capitalize(formatDistance(parsed, new Date(), { addSuffix: true, locale: es })) : 'N/A';
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const dateVal = row.row.original?.createdAt;
                                const dateStr = typeof dateVal === 'object' && dateVal?.date ? dateVal.date : dateVal;
                                if (!dateStr) return 'N/A';
                                const parsed = new Date(dateStr);
                                return !isNaN(parsed.getTime()) ? capitalize(formatDistance(parsed, new Date(), { addSuffix: true, locale: es })) : 'N/A';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'inspector_credentials', permission: 'edit_inspector_credentials' },
                            render: (row: any) => (
                                <Tooltip size="sm" content={'Editar'} placement="top" color="invert">
                                    <Link to={`${editBase}/${row.id}/edit`}>
                                        <ActionIcon as="span" size="sm" variant="outline" className="hover:!border-gray-900 hover:text-gray-700">
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Link>
                                </Tooltip>
                            ),
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'inspector_credentials', permission: 'delete_inspector_credentials' },
                            render: (row: any) => (
                                <Tooltip size="sm" content={'Eliminar'} placement="top" color="invert">
                                    <div>
                                        <DeletePopover
                                            title="Eliminar credencial"
                                            description={`¿Eliminar credencial para el producto seleccionado?`}
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip>
                            ),
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage ?? 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros'}>
                <div className="p-4 text-sm text-gray-600">Los filtros de listado usan los parámetros estándar (página, orden).</div>
            </FilterDrawerView>
        </Page>
    );
}

export default InspectorCredentialsList;
